import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
// ── Queries ──────────────────────────────────────────────────────────────────
export function useMaterialsAdmin() {
    return useQuery({
        queryKey: ['director-materials'],
        queryFn: () => apiClient('/materials'),
        staleTime: 1000 * 60 * 3,
    });
}
export function useMaterialFolders() {
    return useQuery({
        queryKey: ['material-folders'],
        queryFn: () => apiClient('/materials/folders'),
        staleTime: 1000 * 60 * 10,
    });
}
export function useCreateFolder() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (dto) => apiClient('/materials/folders', {
            method: 'POST',
            body: JSON.stringify(dto),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['material-folders'] }),
    });
}
export function useUpdateFolder() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, ...dto }) => apiClient(`/materials/folders/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(dto),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['material-folders'] }),
    });
}
export function useDeleteFolder() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => apiClient(`/materials/folders/${id}`, { method: 'DELETE' }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['material-folders'] });
            void queryClient.invalidateQueries({ queryKey: ['director-materials'] });
        },
    });
}
export function useUploadMaterial() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input) => {
            const fd = new FormData();
            fd.append('file', input.file);
            fd.append('folder_id', input.folder_id);
            fd.append('opposition_id', input.opposition_id);
            if (input.name)
                fd.append('name', input.name);
            if (input.description)
                fd.append('description', input.description);
            return apiClient('/materials/upload', {
                method: 'POST',
                body: fd,
            });
        },
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['director-materials'] }),
    });
}
export function useUpdateMaterial() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, ...dto }) => apiClient(`/materials/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(dto),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['director-materials'] }),
    });
}
export function useDeleteMaterial() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => apiClient(`/materials/${id}`, { method: 'DELETE' }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['director-materials'] }),
    });
}
export function useGrantPermission(materialId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ studentId, canDownload }) => apiClient(`/materials/${materialId}/permissions`, {
            method: 'POST',
            body: JSON.stringify({ student_id: studentId, can_download: canDownload }),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['material-permissions', materialId] }),
    });
}
export function useRevokePermission(materialId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (studentId) => apiClient(`/materials/${materialId}/permissions/${studentId}`, { method: 'DELETE' }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['material-permissions', materialId] }),
    });
}
/** Grant a single material to many students in one API call. */
export function useGrantBulkPermissions() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ materialId, studentIds }) => apiClient(`/materials/${materialId}/permissions/bulk`, {
            method: 'POST',
            body: JSON.stringify({ student_ids: studentIds }),
        }),
        onSuccess: (_data, vars) => void queryClient.invalidateQueries({ queryKey: ['material-permissions', vars.materialId] }),
    });
}
/** Grant many materials × many students. Issues bulk calls in parallel. */
export function useGrantManyMaterials() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ materialIds, studentIds, }) => {
            await Promise.all(materialIds.map((mid) => apiClient(`/materials/${mid}/permissions/bulk`, {
                method: 'POST',
                body: JSON.stringify({ student_ids: studentIds }),
            })));
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['material-permissions'] });
        },
    });
}
export function useFolderPermissions(folderId) {
    return useQuery({
        queryKey: ['folder-permissions', folderId],
        queryFn: () => apiClient(`/materials/folders/${folderId}/permissions`),
        enabled: !!folderId,
    });
}
export function useGrantFolderPermission(folderId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ studentId, canDownload }) => apiClient(`/materials/folders/${folderId}/permissions`, {
            method: 'POST',
            body: JSON.stringify({ student_id: studentId, can_download: canDownload }),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['folder-permissions', folderId] }),
    });
}
export function useRevokeFolderPermission(folderId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (studentId) => apiClient(`/materials/folders/${folderId}/permissions/${studentId}`, { method: 'DELETE' }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['folder-permissions', folderId] }),
    });
}
/** Grant a whole folder to many students in one API call (view-only; the
 *  backend never downgrades an existing grant's can_download). */
export function useGrantFolderBulkPermissions(folderId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (studentIds) => apiClient(`/materials/folders/${folderId}/permissions/bulk`, {
            method: 'POST',
            body: JSON.stringify({ student_ids: studentIds }),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['folder-permissions', folderId] }),
    });
}
// ── Folder permissions for PROFESSORS (grant a whole folder to a professor) ──
// The id sent/returned is the professor's user_profiles.id (project convention).
export function useFolderProfessorPermissions(folderId) {
    return useQuery({
        queryKey: ['folder-professor-permissions', folderId],
        queryFn: () => apiClient(`/materials/folders/${folderId}/professor-permissions`),
        enabled: !!folderId,
    });
}
export function useGrantFolderProfessorPermission(folderId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (professorId) => apiClient(`/materials/folders/${folderId}/professor-permissions`, {
            method: 'POST',
            body: JSON.stringify({ professor_id: professorId }),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['folder-professor-permissions', folderId] }),
    });
}
export function useRevokeFolderProfessorPermission(folderId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (professorId) => apiClient(`/materials/folders/${folderId}/professor-permissions/${professorId}`, {
            method: 'DELETE',
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['folder-professor-permissions', folderId] }),
    });
}
//# sourceMappingURL=useMaterialsAdmin.js.map